import React, { useEffect, useRef, useState } from "react";
import { playStartBeep, playEndBeep, startStreamVisualizer, getRecordingStatusBadge } from "../../utils/audio";
import useWavRecorder from "../../hooks/useWavRecorder";

export default function AudioRecorder({
  callNext,
  clicked,
  setTranscript,
  step,
  Beginning,
  Waiting,
  RecordingTime,
  audio,
  MicAudioRef,
  setDisabledButton,
  setRecordedAudioFile,
  handleRecordedBlob,
  setTranscriptLoading,
}) {
  const [timer, setTimer] = useState(Waiting);
  const [initTimer, setInitTimer] = useState(audio ? Beginning : 0);
  const [isRecording, setIsRecording] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(!!audio);
  
  const recordingIntervalRef = useRef(null);
  const stopTimeoutRef = useRef(null);
  const preEndBeepTimeoutRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const visualizerHandleRef = useRef(null);
  const callNextRef = useRef(callNext);
  const canvasRef = useRef(null);
  const finalizeTimeoutRef = useRef(null);
  const recognitionRef = useRef(null);
  const liveTranscriptRef = useRef("");
  const isRecordingRef = useRef(false);
  const speechRestartTimeoutRef = useRef(null);
  const mountedRef = useRef(false);
  const recognitionSessionIdRef = useRef(0);
  const wavRecorder = useWavRecorder({ targetSampleRate: 16000 });

  useEffect(() => {
    callNextRef.current = callNext;
  }, [callNext]);

  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      try { if (speechRestartTimeoutRef.current) { clearTimeout(speechRestartTimeoutRef.current); speechRestartTimeoutRef.current = null; } } catch {}
      try {
        if (recognitionRef.current) {
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.stop();
          recognitionRef.current = null;
        }
      } catch {}
      try {
        if (MicAudioRef?.current) {
          MicAudioRef.current.onloadedmetadata = null;
          MicAudioRef.current.onended = null;
          MicAudioRef.current.ontimeupdate = null;
          MicAudioRef.current.pause?.();
        }
      } catch {}
    };
  }, []);

  useEffect(() => {
    stopRecording();
  }, [clicked]);

  useEffect(() => {
    setTimer(Waiting);
    setIsRecording(false);
    setDisabledButton(true);
  }, [step, Waiting, setDisabledButton]);

  useEffect(() => {
    if (initTimer > 0) {
      const countdown = setTimeout(() => {
        setInitTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearTimeout(countdown);
    } else if (audio) {
      const el = MicAudioRef?.current;
      if (el) {
        el.src = audio;
        el.onloadedmetadata = () => {
          setTimeout(() => {
            try {
              if (!mountedRef.current) return;
              MicAudioRef?.current?.play?.();
              setIsPlayingAudio(true);
            } catch {}
          }, 100);
        };

        el.onended = () => {
          setIsPlayingAudio(false);
          setTimer(Waiting);
        };

        el.ontimeupdate = () => {
          try {
            if (MicAudioRef?.current) {
              setCurrentTime(MicAudioRef.current.currentTime || 0);
            }
          } catch {}
        };
      }
    } else {
      setTimer(Waiting);
    }
  }, [initTimer, audio, Waiting, MicAudioRef]);

  useEffect(() => {
    setCurrentTime(0);

    if (!isPlayingAudio && timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    } else if (timer <= 0 && !isPlayingAudio) {
      startRecording();
      setDisabledButton(false);
    }
  }, [timer, isPlayingAudio, setDisabledButton]);

  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setCurrentTime((prevTime) => prevTime + 0.1);
      }, 100);

      preEndBeepTimeoutRef.current = setTimeout(() => {
        playEndBeep();
      }, Math.max(0, RecordingTime * 1000 - 300));

      stopTimeoutRef.current = setTimeout(() => {
        stopRecording({ autoAdvance: true });
      }, RecordingTime * 1000);
    }

    return () => {
      clearInterval(recordingIntervalRef.current);
      clearTimeout(stopTimeoutRef.current);
      clearTimeout(preEndBeepTimeoutRef.current);
    };
  }, [isRecording, RecordingTime]);


  const startRecording = async () => {
    await playStartBeep();
    try {
      try { setTranscript(""); } catch {}
      const { stream } = await wavRecorder.start();
      mediaStreamRef.current = stream;

      visualizerHandleRef.current = await startStreamVisualizer({
        stream,
        canvas: canvasRef.current,
        options: { fftSize: 2048, smoothing: 0.94, lineWidth: 2, style: 'bars', barWidth: 1, barGap: 2, mirror: true, origin: 'center' }
      });

      setIsRecording(true);
      isRecordingRef.current = true;

      // Live transcription (Chrome)
      liveTranscriptRef.current = '';
      setTranscriptLoading(true);
      startLiveRecognition();
    } catch (err) {
      console.error("Failed to start recorder:", err);
    }
  };

  const stopRecording = async ({ autoAdvance = false } = {}) => {
    if (!isRecordingRef.current) return;
    
    setIsRecording(false);
    isRecordingRef.current = false;
    const stopStartedAt = Date.now();
    clearTimeout(stopTimeoutRef.current);
    stopTimeoutRef.current = null;
    clearTimeout(preEndBeepTimeoutRef.current);
    preEndBeepTimeoutRef.current = null;

    try { visualizerHandleRef.current?.cleanup(); } catch {}

    try {
      if (speechRestartTimeoutRef.current) { clearTimeout(speechRestartTimeoutRef.current); speechRestartTimeoutRef.current = null; }
      if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.onerror = null; recognitionRef.current.stop(); recognitionRef.current = null; }
    } catch {}
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }

    // Stop WAV hook and get blob
    try {
      const result = await wavRecorder.stop();
      if (result && result.blob) {
        if (handleRecordedBlob) handleRecordedBlob(result.blob);
        const file = new File([result.blob], 'recording.wav', { type: 'audio/wav', lastModified: Date.now() });
        setRecordedAudioFile(file);
      }
    } catch (e) {
      console.error('WAV stop failed:', e);
    }

    // a short grace period for final onresult to arrive before finalize
    if (finalizeTimeoutRef.current) clearTimeout(finalizeTimeoutRef.current);
    const idleThresholdMs = 150;
    const maxWaitMs = 700;
    const finalizeIfIdle = () => {
      const now = Date.now();
      const finalText = (liveTranscriptRef.current || '').trim();
      const sinceStop = now - stopStartedAt;
      if (finalText) {
        setTranscript(finalText);
      }
      if (sinceStop >= idleThresholdMs || sinceStop >= maxWaitMs) {
        setTranscriptLoading(false);
        if (autoAdvance) {
          callNextRef.current();
        }
      } else {
        finalizeTimeoutRef.current = setTimeout(finalizeIfIdle, 90);
      }
    };
    finalizeTimeoutRef.current = setTimeout(finalizeIfIdle, 220);
  };

  function startLiveRecognition() {
    if (!('webkitSpeechRecognition' in window)) return;
    if (!mountedRef.current || !isRecordingRef.current) return;
    try {
      const recId = ++recognitionSessionIdRef.current;
      const SpeechRecognition = window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      // console.log('[Speech] Starting live recognition');

      recognition.onresult = (event) => {
        if (recId !== recognitionSessionIdRef.current) return;
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            const chunk = event.results[i][0].transcript;
            liveTranscriptRef.current += chunk + ' ';
            // console.log('[Speech] Final chunk:', chunk);
          }
        }
      };

      recognition.onerror = (e) => {
        if (recId !== recognitionSessionIdRef.current) return;
        console.warn('[Speech] onerror:', e?.error || e);
        // If no-speech or network hiccup, try to restart quickly while recording
        if (mountedRef.current && isRecordingRef.current) {
          if (speechRestartTimeoutRef.current) clearTimeout(speechRestartTimeoutRef.current);
          speechRestartTimeoutRef.current = setTimeout(() => {
            // console.log('[Speech] Restarting after error');
            try { recognitionRef.current && recognitionRef.current.stop(); } catch {}
            if (!mountedRef.current || !isRecordingRef.current) return;
            startLiveRecognition();
          }, 150);
        }
      };

      recognition.onend = () => {
        if (recId !== recognitionSessionIdRef.current) return;
        // console.log('[Speech] onend');
        if (mountedRef.current && isRecordingRef.current) {
          if (speechRestartTimeoutRef.current) clearTimeout(speechRestartTimeoutRef.current);
          speechRestartTimeoutRef.current = setTimeout(() => {
            // console.log('[Speech] Restarting after end');
            if (!mountedRef.current || !isRecordingRef.current) return;
            startLiveRecognition();
          }, 100);
        }
      };

      recognition.start();
      recognitionRef.current = recognition;
    } catch {}
  }

  // ------------------ Render ------------------ //
  const totalDuration = (isPlayingAudio && MicAudioRef.current?.duration)
    ? MicAudioRef.current.duration
    : (RecordingTime || 40);
  const progressPercent = totalDuration > 0 ? Math.min(100, Math.max(0, (currentTime / totalDuration) * 100)) : 0;
  
  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          backgroundColor: "rgba(153, 108, 254, 0.10)",
          maxWidth: "350px",
          borderRadius: "8px",
          padding: "15px",
          margin: "30px auto",
        }}
      >
        <StatusRow>
          {(() => {
            const { text, color } = getRecordingStatusBadge({
              hasAudio: !!audio,
              initTimer,
              isPlayingAudio,
              timer,
              isRecording,
            });
            return (
              <StatusBadge color={color}>{text}</StatusBadge>
            );
          })()}
        </StatusRow>

        <div>
          <audio ref={MicAudioRef}>
            Your browser does not support the audio element.
          </audio>
          <ProgressWrapper>
            <ProgressTrack />
            <ProgressFill percent={progressPercent} />
            <ProgressThumb percent={progressPercent} />
            <input
              type="range"
              value={currentTime}
              min="0"
              max={
                isPlayingAudio && MicAudioRef.current?.duration
                  ? MicAudioRef.current.duration
                  : RecordingTime || 40
              }
              step="0.1"
              className="slider"
              onChange={() => {}}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                width: '100%',
                height: 18,
                opacity: 0,
                cursor: 'default',
              }}
            />
          </ProgressWrapper>
          <VisualizerContainer expanded={isRecording}>
            <canvas ref={canvasRef} width={300} height={80} style={{ width: '100%', height: '100%', display: 'block' }} />
          </VisualizerContainer>
        </div>
      </div>
    </div>
  );
}

// ------------------ Styled Components ------------------ //
function StatusRow({ children }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>{children}</div>
  );
}

function StatusBadge({ color, children }) {
  return (
    <span
      style={{
        fontSize: "12px",
        padding: "3px 8px",
        borderRadius: "999px",
        color: "#fff",
        backgroundColor: color,
      }}
    >
      {children}
    </span>
  );
}

function ProgressWrapper({ children }) {
  return (
    <div style={{ position: 'relative', width: '100%', height: 18, display: 'flex', alignItems: 'center', marginTop: 10 }}>{children}</div>
  );
}

function ProgressTrack() {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, height: 6, background: '#e2e2ea', borderRadius: 4 }} />
  );
}

function ProgressFill({ percent }) {
  return (
    <div style={{ position: 'absolute', left: 0, width: `${percent}%`, height: 6, background: '#996cfe', borderRadius: 4 }} />
  );
}

function ProgressThumb({ percent }) {
  return (
    <div style={{ position: 'absolute', left: `calc(${percent}% - 6px)`, width: 12, height: 12, borderRadius: 999, background: '#996cfe' }} />
  );
}

function VisualizerContainer({ expanded, children }) {
  return (
    <div
      style={{
        width: '100%',
        height: expanded ? 70 : 0,
        overflow: 'hidden',
        transition: 'height 1s ease',
        marginTop: expanded ? 8 : 0,
      }}
    >
      {children}
    </div>
  );
}